import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import './PackageSelector.css';

export interface PackageData {
    id: number;
    name: string;
    nameAr: string;
    packType: 'forfait' | 'abonnement';
    price: number;
    yearlyPrice: number | null;
    questionsCount: number | null;
    icon: string;
    features: string[];
    featuresAr: string[];
    isPopular: boolean;
    color: string;
    requireQuote: boolean;
    isTest?: boolean;
    isNbPosteEnabled?: boolean;
}

interface PackageSelectorProps {
    selectedPlan: number | null;
    onPlanSelect: (planId: number) => void;
    billingPeriod: 'monthly' | 'annual';
    onBillingPeriodChange: (period: 'monthly' | 'annual') => void;
    disabled?: boolean;
    showBillingToggle?: boolean;
    onPackagesLoaded?: (packages: PackageData[]) => void;
}

const PackageSelector: React.FC<PackageSelectorProps> = ({
    selectedPlan,
    onPlanSelect,
    billingPeriod,
    onBillingPeriodChange,
    disabled = false,
    showBillingToggle = true,
    onPackagesLoaded
}) => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';

    const [forfaits, setForfaits] = useState<PackageData[]>([]);
    const [abonnements, setAbonnements] = useState<PackageData[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPackages = async () => {
            try {
                const res = await api.get('/packages');
                const allPkgs = (res.data.all || []).filter((p: PackageData) => !p.isTest);
                setForfaits((res.data.forfaits || []).filter((p: PackageData) => !p.isTest));
                setAbonnements((res.data.abonnements || []).filter((p: PackageData) => !p.isTest));
                if (onPackagesLoaded) onPackagesLoaded(allPkgs);
            } catch (err) {
                console.error('Error fetching packages:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchPackages();
    }, []);

    const getPrice = (pkg: PackageData) => {
        if (pkg.packType === 'forfait') return pkg.price;
        return billingPeriod === 'monthly' ? pkg.price : pkg.yearlyPrice;
    };

    const getPeriodLabel = (pkg: PackageData) => {
        if (pkg.packType === 'forfait') return '';
        return billingPeriod === 'monthly'
            ? t('package.perMonth')
            : t('package.perYear');
    };

    if (loading) {
        return (
            <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    return (
        <div className="package-selector">
            {/* Billing Toggle for Subscriptions */}
            {showBillingToggle && (
                <div className="billing-toggle mb-4">
                    <button
                        className={`toggle-btn ${billingPeriod === 'monthly' ? 'active' : ''}`}
                        onClick={() => onBillingPeriodChange('monthly')}
                        disabled={disabled}
                    >
                        {t('package.monthly')}
                    </button>
                    <button
                        className={`toggle-btn ${billingPeriod === 'annual' ? 'active' : ''}`}
                        onClick={() => onBillingPeriodChange('annual')}
                        disabled={disabled}
                    >
                        {t('package.annual')}
                        <span className="save-badge">{t('package.twoMonthsFree')}</span>
                    </button>
                </div>
            )}

            {/* Forfaits */}
            {forfaits.length > 0 && (
                <div className="mb-4">
                    <h6 className="section-subtitle mb-3">
                        <i className="bi bi-coin text-warning me-2"></i>
                        {t('package.forfaits')}
                    </h6>
                    <div className="plans-grid">
                        {forfaits.map(pkg => (
                            <div
                                key={pkg.id}
                                className={`plan-card ${selectedPlan === pkg.id ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                                style={{ '--plan-color': pkg.color } as React.CSSProperties}
                                onClick={() => !disabled && onPlanSelect(pkg.id)}
                            >
                                <div className="plan-icon"><i className={`bi ${pkg.icon}`}></i></div>
                                <h5 className="plan-name">{isArabic ? pkg.nameAr : pkg.name}</h5>
                                <div className="plan-price">
                                    <span className="amount">{getPrice(pkg)?.toLocaleString()}</span>
                                    <span className="currency">DA</span>
                                </div>
                                {pkg.questionsCount && (
                                    <p className="plan-questions">{pkg.questionsCount} {t('package.questions')}</p>
                                )}
                                {selectedPlan === pkg.id && <i className="bi bi-check-circle-fill selected-check"></i>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Abonnements */}
            {abonnements.length > 0 && (
                <div className="mb-4">
                    <h6 className="section-subtitle mb-3">
                        <i className="bi bi-calendar-check text-primary me-2"></i>
                        {t('package.subscriptions')}
                    </h6>
                    <div className="plans-grid">
                        {abonnements.map(pkg => (
                            <div
                                key={pkg.id}
                                className={`plan-card ${selectedPlan === pkg.id ? 'selected' : ''} ${pkg.isPopular ? 'popular' : ''} ${disabled ? 'disabled' : ''}`}
                                style={{ '--plan-color': pkg.color } as React.CSSProperties}
                                onClick={() => !disabled && onPlanSelect(pkg.id)}
                            >
                                {pkg.isPopular && (
                                    <span className="popular-badge">{t('package.popular')}</span>
                                )}
                                <div className="plan-icon"><i className={`bi ${pkg.icon}`}></i></div>
                                <h5 className="plan-name">{isArabic ? pkg.nameAr : pkg.name}</h5>
                                {pkg.requireQuote ? (
                                    <div className="plan-price">
                                        <span className="custom-price">{t('package.custom')}</span>
                                    </div>
                                ) : (
                                    <div className="plan-price">
                                        <span className="amount">{getPrice(pkg)?.toLocaleString()}</span>
                                        <span className="currency">DA</span>
                                        <span className="period">{getPeriodLabel(pkg)}</span>
                                    </div>
                                )}
                                {selectedPlan === pkg.id && <i className="bi bi-check-circle-fill selected-check"></i>}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PackageSelector;
